import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "./baseApi";

// ── Types ─────────────────────────────────────────────────────────────────────

export type StockAuditStatus =
  | "DRAFT"
  | "IN_PROGRESS"
  | "PAUSED"
  | "PENDING_APPROVAL"
  | "APPROVED"
  | "CLOSED";

export type StockAuditType = "FULL" | "SPOT";
export type ScheduleFrequency = "ONCE" | "WEEKLY" | "MONTHLY";

export interface StockAudit {
  id: string;
  auditCode: string;
  storeId: string;
  outletId: string;
  scheduleId?: string | null;
  type: StockAuditType;
  status: StockAuditStatus;
  notes?: string | null;
  rejectionReason?: string | null;
  assignedTo?: string | null;
  createdBy: string;
  approvedBy?: string | null;
  rejectedBy?: string | null;
  snapshotAt?: string | null;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  closedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  scopeCategoryIds: string[];
  scopeSubcategoryIds: string[];
  scopeEtalaseIds: string[];
  scopeDistributorIds: string[];
  scopeItemIds: string[];
  blockTransactions: boolean;
  creator?: { id: string; name: string; username: string };
  assignee?: { id: string; name: string; username: string } | null;
  approver?: { id: string; name: string; username: string } | null;
  rejecter?: { id: string; name: string; username: string } | null;
  outlet?: { id: string; name: string };
  store?: { id: string; name: string };
  schedule?: StockAuditSchedule | null;
  _count?: { items: number };
  summary?: AuditSummary;
}

export interface AuditSummary {
  total: number;
  counted: number;
  uncounted: number;
  withDiscrepancy: number;
}

export interface StockAuditItem {
  id: string;
  stockAuditId: string;
  itemId: string;
  systemQuantity: string;
  countedQuantity?: string | null;
  discrepancy?: string | null;
  isCounted: boolean;
  hasPendingTransfer: boolean;
  pendingTransferQty: string;
  notes?: string | null;
  countedBy?: string | null;
  countedAt?: string | null;
  item?: {
    id: string;
    name: string;
    sku: string;
    barcode?: string | null;
    baseUnit: string;
  };
  counter?: { id: string; name: string; username: string } | null;
}

export interface StockAuditLog {
  id: string;
  stockAuditId: string;
  userId?: string | null;
  action: string;
  itemId?: string | null;
  oldValue?: string | null;
  newValue?: string | null;
  notes?: string | null;
  createdAt: string;
  user?: { id: string; name: string; username: string } | null;
  item?: { id: string; name: string; sku: string } | null;
}

export interface StockAuditSchedule {
  id: string;
  storeId: string;
  outletId: string;
  name: string;
  type: StockAuditType;
  frequency: ScheduleFrequency;
  scheduledDate?: string | null;
  dayOfWeek?: number | null;
  dayOfMonth?: number | null;
  notes?: string | null;
  assignedTo?: string | null;
  isActive: boolean;
  nextRunAt: string;
  lastRunAt?: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  scopeCategoryIds: string[];
  scopeSubcategoryIds: string[];
  scopeEtalaseIds: string[];
  scopeDistributorIds: string[];
  scopeItemIds: string[];
  creator?: { id: string; name: string; username: string };
  assignee?: { id: string; name: string; username: string } | null;
  outlet?: { id: string; name: string };
  _count?: { audits: number };
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface StockAuditFilters {
  page?: number;
  limit?: number;
  storeId?: string;
  outletId?: string;
  status?: StockAuditStatus;
  type?: StockAuditType;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface AuditItemFilters {
  page?: number;
  limit?: number;
  uncountedOnly?: boolean;
  discrepancyOnly?: boolean;
  search?: string;
  categoryId?: string;
  subcategoryId?: string;
}

export interface ScheduleFilters {
  page?: number;
  limit?: number;
  storeId?: string;
  outletId?: string;
  isActive?: boolean;
  frequency?: ScheduleFrequency;
}

export interface CreateStockAuditData {
  storeId: string;
  outletId: string;
  type?: StockAuditType;
  notes?: string;
  assignedTo?: string;
  scopeCategoryIds?: string[];
  scopeSubcategoryIds?: string[];
  scopeEtalaseIds?: string[];
  scopeDistributorIds?: string[];
  scopeItemIds?: string[];
}

export interface CreateScheduleData {
  storeId: string;
  outletId: string;
  name: string;
  type: StockAuditType;
  frequency: ScheduleFrequency;
  scheduledDate?: string;
  dayOfWeek?: number;
  dayOfMonth?: number;
  notes?: string;
  assignedTo?: string;
  scopeCategoryIds?: string[];
  scopeSubcategoryIds?: string[];
  scopeEtalaseIds?: string[];
  scopeDistributorIds?: string[];
  scopeItemIds?: string[];
}

// ── API slice ─────────────────────────────────────────────────────────────────

export const stockAuditApi = createApi({
  reducerPath: "stockAuditApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["StockAudit", "StockAuditItem", "StockAuditLog", "StockAuditSchedule"],
  endpoints: builder => ({
    // ── Session list ──────────────────────────────────────────────────────────
    getStockAudits: builder.query<
      { success: boolean; data: { audits: StockAudit[]; pagination: PaginationMeta } },
      StockAuditFilters | void
    >({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null) searchParams.append(k, String(v));
          });
        }
        return `/stock-audits?${searchParams.toString()}`;
      },
      providesTags: result =>
        result?.data?.audits
          ? [
              ...result.data.audits.map(({ id }) => ({ type: "StockAudit" as const, id })),
              { type: "StockAudit", id: "LIST" },
            ]
          : [{ type: "StockAudit", id: "LIST" }],
    }),

    // ── Due count (scoped by CASL — shows across all accessible outlets) ──────
    getStockAuditDueCount: builder.query<
      { success: boolean; data: { scheduledDue: number; pendingApproval: number } },
      void
    >({
      query: () => `/stock-audits/due`,
      providesTags: [{ type: "StockAudit", id: "DUE" }],
    }),

    // ── Session detail ────────────────────────────────────────────────────────
    getStockAuditById: builder.query<{ success: boolean; data: StockAudit }, string>({
      query: id => `/stock-audits/${id}`,
      providesTags: (_result, _error, id) => [{ type: "StockAudit", id }],
    }),

    // ── Create session ────────────────────────────────────────────────────────
    createStockAudit: builder.mutation<
      { success: boolean; data: StockAudit; message: string },
      CreateStockAuditData
    >({
      query: data => ({ url: "/stock-audits", method: "POST", body: data }),
      invalidatesTags: [{ type: "StockAudit", id: "LIST" }],
    }),

    // ── Delete session ────────────────────────────────────────────────────────
    deleteStockAudit: builder.mutation<{ success: boolean; message: string }, string>({
      query: id => ({ url: `/stock-audits/${id}`, method: "DELETE" }),
      invalidatesTags: (_result, _error, id) => [
        { type: "StockAudit", id },
        { type: "StockAudit", id: "LIST" },
      ],
    }),

    // ── Lifecycle mutations ───────────────────────────────────────────────────
    startAudit: builder.mutation<{ success: boolean; data: StockAudit }, string>({
      query: id => ({ url: `/stock-audits/${id}/start`, method: "PATCH" }),
      invalidatesTags: (_result, _error, id) => [
        { type: "StockAudit", id },
        { type: "StockAudit", id: "LIST" },
        { type: "StockAuditItem", id: `LIST_${id}` },
      ],
    }),

    pauseAudit: builder.mutation<{ success: boolean; data: StockAudit }, string>({
      query: id => ({ url: `/stock-audits/${id}/pause`, method: "PATCH" }),
      invalidatesTags: (_result, _error, id) => [{ type: "StockAudit", id }],
    }),

    resumeAudit: builder.mutation<{ success: boolean; data: StockAudit }, string>({
      query: id => ({ url: `/stock-audits/${id}/resume`, method: "PATCH" }),
      invalidatesTags: (_result, _error, id) => [{ type: "StockAudit", id }],
    }),

    submitAudit: builder.mutation<
      { success: boolean; data: StockAudit; uncountedCount?: number },
      string
    >({
      query: id => ({ url: `/stock-audits/${id}/submit`, method: "PATCH" }),
      invalidatesTags: (_result, _error, id) => [
        { type: "StockAudit", id },
        { type: "StockAudit", id: "LIST" },
      ],
    }),

    approveAudit: builder.mutation<
      { success: boolean; data: StockAudit; message: string },
      string
    >({
      query: id => ({ url: `/stock-audits/${id}/approve`, method: "PATCH" }),
      invalidatesTags: (_result, _error, id) => [
        { type: "StockAudit", id },
        { type: "StockAudit", id: "LIST" },
      ],
    }),

    rejectAudit: builder.mutation<
      { success: boolean; data: StockAudit },
      { id: string; rejectionReason: string }
    >({
      query: ({ id, rejectionReason }) => ({
        url: `/stock-audits/${id}/reject`,
        method: "PATCH",
        body: { rejectionReason },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "StockAudit", id },
        { type: "StockAudit", id: "LIST" },
      ],
    }),

    closeAudit: builder.mutation<{ success: boolean; data: StockAudit }, string>({
      query: id => ({ url: `/stock-audits/${id}/close`, method: "PATCH" }),
      invalidatesTags: (_result, _error, id) => [
        { type: "StockAudit", id },
        { type: "StockAudit", id: "LIST" },
      ],
    }),

    // ── Item counting ─────────────────────────────────────────────────────────
    getAuditItems: builder.query<
      { success: boolean; data: { items: StockAuditItem[]; pagination: PaginationMeta } },
      { auditId: string; filters?: AuditItemFilters }
    >({
      query: ({ auditId, filters = {} }) => {
        const searchParams = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
          if (v !== undefined && v !== null) searchParams.append(k, String(v));
        });
        return `/stock-audits/${auditId}/items?${searchParams.toString()}`;
      },
      providesTags: (_result, _error, { auditId }) => [
        { type: "StockAuditItem", id: `LIST_${auditId}` },
      ],
    }),

    updateItemCount: builder.mutation<
      { success: boolean; data: StockAuditItem },
      { auditId: string; itemId: string; countedQuantity: number; notes?: string }
    >({
      query: ({ auditId, itemId, ...body }) => ({
        url: `/stock-audits/${auditId}/items/${itemId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { auditId }) => [
        { type: "StockAuditItem", id: `LIST_${auditId}` },
        { type: "StockAudit", id: auditId },
      ],
    }),

    addAuditItem: builder.mutation<
      { success: boolean; data: StockAuditItem; alreadyExists: boolean },
      { auditId: string; barcode?: string; itemId?: string }
    >({
      query: ({ auditId, ...body }) => ({
        url: `/stock-audits/${auditId}/items`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { auditId }) => [
        { type: "StockAuditItem", id: `LIST_${auditId}` },
      ],
    }),

    removeAuditItem: builder.mutation<
      { success: boolean; message: string },
      { auditId: string; itemId: string }
    >({
      query: ({ auditId, itemId }) => ({
        url: `/stock-audits/${auditId}/items/${itemId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { auditId }) => [
        { type: "StockAuditItem", id: `LIST_${auditId}` },
      ],
    }),

    // ── Logs ──────────────────────────────────────────────────────────────────
    getAuditLogs: builder.query<{ success: boolean; data: StockAuditLog[] }, string>({
      query: id => `/stock-audits/${id}/logs`,
      providesTags: (_result, _error, id) => [{ type: "StockAuditLog", id }],
    }),

    // ── Block transactions toggle ──────────────────────────────────────────────
    toggleAuditBlock: builder.mutation<
      { success: boolean; data: StockAudit },
      { id: string; block: boolean }
    >({
      query: ({ id, block }) => ({
        url: `/stock-audits/${id}/block-transactions`,
        method: "PATCH",
        body: { block },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "StockAudit", id }],
    }),

    // ── Outlet block status ────────────────────────────────────────────────────
    getOutletBlockStatus: builder.query<
      { success: boolean; data: { isBlocked: boolean; auditCode: string | null } },
      string
    >({
      query: outletId => `/stock-audits/outlet-block?outletId=${outletId}`,
      providesTags: [{ type: "StockAudit", id: "BLOCK" }],
    }),

    // ── Item audit history ─────────────────────────────────────────────────────
    getItemAuditHistory: builder.query<
      { success: boolean; data: StockAuditItem[] },
      string
    >({
      query: itemId => `/stock-audits/item-history/${itemId}`,
      providesTags: (_result, _error, itemId) => [{ type: "StockAuditItem", id: `HISTORY_${itemId}` }],
    }),

    // ── Schedules ─────────────────────────────────────────────────────────────
    getStockAuditSchedules: builder.query<
      {
        success: boolean;
        data: { schedules: StockAuditSchedule[]; pagination: PaginationMeta };
      },
      ScheduleFilters | void
    >({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null) searchParams.append(k, String(v));
          });
        }
        return `/stock-audits/schedules?${searchParams.toString()}`;
      },
      providesTags: result =>
        result?.data?.schedules
          ? [
              ...result.data.schedules.map(({ id }) => ({
                type: "StockAuditSchedule" as const,
                id,
              })),
              { type: "StockAuditSchedule", id: "LIST" },
            ]
          : [{ type: "StockAuditSchedule", id: "LIST" }],
    }),

    getStockAuditScheduleById: builder.query<
      { success: boolean; data: StockAuditSchedule },
      string
    >({
      query: id => `/stock-audits/schedules/${id}`,
      providesTags: (_result, _error, id) => [{ type: "StockAuditSchedule", id }],
    }),

    createStockAuditSchedule: builder.mutation<
      { success: boolean; data: StockAuditSchedule; message: string },
      CreateScheduleData
    >({
      query: data => ({ url: "/stock-audits/schedules", method: "POST", body: data }),
      invalidatesTags: [{ type: "StockAuditSchedule", id: "LIST" }],
    }),

    updateStockAuditSchedule: builder.mutation<
      { success: boolean; data: StockAuditSchedule; message: string },
      { id: string; data: Partial<CreateScheduleData> }
    >({
      query: ({ id, data }) => ({
        url: `/stock-audits/schedules/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "StockAuditSchedule", id },
        { type: "StockAuditSchedule", id: "LIST" },
      ],
    }),

    toggleStockAuditSchedule: builder.mutation<
      { success: boolean; data: StockAuditSchedule; message: string },
      string
    >({
      query: id => ({ url: `/stock-audits/schedules/${id}/toggle`, method: "PATCH" }),
      invalidatesTags: (_result, _error, id) => [
        { type: "StockAuditSchedule", id },
        { type: "StockAuditSchedule", id: "LIST" },
      ],
    }),

    deleteStockAuditSchedule: builder.mutation<{ success: boolean; message: string }, string>({
      query: id => ({ url: `/stock-audits/schedules/${id}`, method: "DELETE" }),
      invalidatesTags: (_result, _error, id) => [
        { type: "StockAuditSchedule", id },
        { type: "StockAuditSchedule", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetStockAuditsQuery,
  useGetStockAuditDueCountQuery,
  useGetStockAuditByIdQuery,
  useCreateStockAuditMutation,
  useDeleteStockAuditMutation,
  useStartAuditMutation,
  usePauseAuditMutation,
  useResumeAuditMutation,
  useSubmitAuditMutation,
  useApproveAuditMutation,
  useRejectAuditMutation,
  useCloseAuditMutation,
  useGetAuditItemsQuery,
  useUpdateItemCountMutation,
  useAddAuditItemMutation,
  useRemoveAuditItemMutation,
  useGetAuditLogsQuery,
  useToggleAuditBlockMutation,
  useGetOutletBlockStatusQuery,
  useGetItemAuditHistoryQuery,
  useGetStockAuditSchedulesQuery,
  useGetStockAuditScheduleByIdQuery,
  useCreateStockAuditScheduleMutation,
  useUpdateStockAuditScheduleMutation,
  useToggleStockAuditScheduleMutation,
  useDeleteStockAuditScheduleMutation,
} = stockAuditApi;
